/**
 * Marketing Report Page
 * 
 * Displays ad spend breakdown by channel with visualizations
 */

import { parseISO, format } from 'date-fns'
import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
  AreaChart, Area
} from 'recharts'
import { marketingAPI, trafficAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { DateRangePicker } from '../components/DateRangePicker'
import AggregateMarketingView from '../components/AggregateMarketingView'
import DataFreshnessWarning from '../components/DataFreshnessWarning'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button, Badge
} from '../components/ui'
import NoAdSpendNotice from '../components/NoAdSpendNotice'
import { channelColor } from '../lib/channels'
import { QualitySparkline } from '../components/Sparklines'
import CampaignBreakdown from '../components/CampaignBreakdown'

function DeltaBadge({ current, prior, invert = false }) {
  if (!prior || prior === 0) return null
  const pct = ((current - prior) / prior) * 100
  if (!isFinite(pct)) return null
  const isPositive = pct > 0
  // invert: for cost metrics, increase = bad (red), decrease = good (green)
  const isGood = invert ? !isPositive : isPositive
  return (
    <span className={`inline-flex items-center text-xs font-medium ${isGood ? 'text-green-600' : 'text-red-500'}`}>
      {isPositive ? '↑' : '↓'} {Math.abs(pct).toFixed(1)}%
    </span>
  )
}

// Channel colours come from lib/channels — the registry that also knows the
// spend-only channels (youtube, bing, snapchat, twitter, linkedin, pinterest)
// this page renders straight out of ad_spend, and cycles a default colour for
// anything the ingestion Lambda adds that nothing has a colour for yet.
const getChannelColor = channelColor

// Format currency
function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

// Format large numbers
function formatNumber(value) {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  return value.toFixed(0)
}

// Custom tooltip for charts
function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium text-popover-foreground mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="flex justify-between gap-4">
            <span>{entry.name}:</span>
            <span className="font-medium">{formatCurrency(entry.value)}</span>
          </p>
        ))}
      </div>
    )
  }
  return null
}

// Pie chart custom label (for donut chart - labels on the arc)
function renderCustomLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) {
  if (percent < 0.08) return null // Don't show labels for small slices
  
  const RADIAN = Math.PI / 180
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      className="text-xs font-bold"
      style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  )
}

// Custom tooltip for pie chart (works in dark mode)
function PieTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const data = payload[0]
    return (
      <div className="bg-popover text-popover-foreground border border-border rounded-lg shadow-xl p-3 text-sm z-50 relative">
        <div className="flex items-center gap-2 mb-1">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: data.payload.color }}
          />
          <span className="font-semibold">{data.name}</span>
        </div>
        <div className="text-lg font-bold">{formatCurrency(data.value)}</div>
      </div>
    )
  }
  return null
}

// ad_spend channel ids → traffic source ids. Mostly identical; google_bigquery
// is the same channel arriving via a second ingest path.
const CHANNEL_TO_TRAFFIC_SOURCE = {
  google_bigquery: 'google',
}

function trafficSourceForChannel(channel) {
  const c = String(channel || '').toLowerCase()
  return CHANNEL_TO_TRAFFIC_SOURCE[c] || c
}

// The gate is its own component so the page's hooks always run in the same
// order: hooks after an early return crash React when the condition flips.
// Switching to All Clinics while on this page used to do exactly that.
export default function MarketingPage() {
  const { currentClinicId } = useAuthStore()
  // All Clinics selected: the aggregate view.
  if (currentClinicId === null) return <AggregateMarketingView />
  return <ClinicMarketingPage />
}

function ClinicMarketingPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentClinicId } = useAuthStore()

  // Date range state
  const [dateRange, setDateRange] = useState(() => {
    const startParam = searchParams.get('startDate')
    const endParam = searchParams.get('endDate')
    
    if (startParam && endParam) {
      return {
        from: parseISO(startParam),
        to: parseISO(endParam)
      }
    }
    
    // Default to last 30 days
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 29)
    return { from: start, to: end }
  })
  
  // Data state
  const [data, setData] = useState(null)
  const [trends, setTrends] = useState(null)
  const [campaigns, setCampaigns] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Traffic-quality sparklines, keyed by channel and by ad name. Fetched
  // separately from the spend report: it hits BigQuery and takes seconds, and
  // there is no reason to hold the whole page behind it.
  const [quality, setQuality] = useState(null)

  // Fetch data when date range or clinic changes
  useEffect(() => {
    if (currentClinicId && dateRange?.from && dateRange?.to) {
      fetchData()
    }
  }, [currentClinicId, dateRange])

  // Load quality sparklines once the spend report is in — the ad names to match
  // come from the campaign tree, so this necessarily follows it. Brands without
  // BigQuery traffic data just 400 here; the sparklines stay absent and the
  // report is otherwise unaffected.
  useEffect(() => {
    if (!data && !campaigns) return
    if (!dateRange?.from || !dateRange?.to) return

    let cancelled = false
    const adNames = [...new Set(
      (campaigns?.campaigns || [])
        .flatMap(c => c.adsets || [])
        .flatMap(a => a.ads || [])
        .map(ad => ad.ad_name)
        .filter(Boolean)
    )]

    trafficAPI.getQualitySparklines({
      start: format(dateRange.from, 'yyyy-MM-dd'),
      end: format(dateRange.to, 'yyyy-MM-dd'),
      interval: 'day',
      adNames,
    })
      .then(res => { if (!cancelled && res.data?.success) setQuality(res.data.data) })
      .catch(() => { if (!cancelled) setQuality(null) })

    return () => { cancelled = true }
  }, [data, campaigns, dateRange, currentClinicId])

  // Update URL params when date range changes
  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      const params = new URLSearchParams(searchParams)
      params.set('startDate', format(dateRange.from, 'yyyy-MM-dd'))
      params.set('endDate', format(dateRange.to, 'yyyy-MM-dd'))
      setSearchParams(params, { replace: true })
    }
  }, [dateRange])

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    setQuality(null)

    try {
      const params = {
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd')
      }
      
      const [spendRes, trendsRes, campaignsRes] = await Promise.all([
        marketingAPI.getSpend(params),
        marketingAPI.getTrends({ weeks: 8 }),
        marketingAPI.getCampaigns(params).catch(() => null)
      ])

      setData(spendRes.data)
      setTrends(trendsRes.data.trends)
      setCampaigns(campaignsRes?.data || null)
    } catch (err) {
      console.error('Error fetching marketing data:', err)
      setError(err.response?.data?.error?.message || err.response?.data?.error || 'Failed to fetch marketing data')
    } finally {
      setLoading(false)
    }
  }

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange)
  }

  if (!currentClinicId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Clinic Selection Required</CardTitle>
            <CardDescription>
              Please select a clinic from the header dropdown to view marketing data.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  // Prepare chart data
  const pieChartData = data?.by_channel?.map((ch, index) => ({
    name: ch.name,
    value: ch.spend,
    color: getChannelColor(ch.channel, index)
  })) || []

  // Daily spend chart data
  const dailyChartData = data?.daily?.map(day => {
    const entry = { date: day.date.substring(5) } // MM-DD format
    data.by_channel?.forEach(ch => {
      entry[ch.name] = day.channels[ch.channel]?.spend || 0
    })
    return entry
  }) || []

  // Trends chart data
  const trendsChartData = trends?.map(t => ({
    week: t.week,
    spend: t.spend,
    clicks: t.clicks
  })) || []

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <NoAdSpendNotice />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Marketing</h1>
          <p className="text-muted-foreground mt-1">
            Ad spend breakdown by channel
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
          />
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </div>

      <DataFreshnessWarning
        daily={data?.daily}
        dateRange={dateRange}
        loading={loading}
      />

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading marketing data...</p>
          </div>
        </div>
      ) : data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Spend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold">{formatCurrency(data.summary.total_spend)}</span>
                  <DeltaBadge current={data.summary.total_spend} prior={data.summary.prior_period?.total_spend} invert />
                </div>
                <p className="text-xs text-muted-foreground">
                  {data.date_range?.days || 0} days · vs prior period
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Clicks
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold">{formatNumber(data.summary.total_clicks)}</span>
                  <DeltaBadge current={data.summary.total_clicks} prior={data.summary.prior_period?.total_clicks} />
                </div>
                <p className="text-xs text-muted-foreground">
                  {data.by_channel?.length || 0} channels
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Avg CPC
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold">${data.summary.cpc?.toFixed(2) || '0.00'}</span>
                  <DeltaBadge current={data.summary.cpc} prior={data.summary.prior_period?.cpc} invert />
                </div>
                <p className="text-xs text-muted-foreground">
                  Cost per click
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Impressions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold">{formatNumber(data.summary.total_impressions)}</span>
                  <DeltaBadge current={data.summary.total_impressions} prior={data.summary.prior_period?.total_impressions} />
                </div>
                <p className="text-xs text-muted-foreground">
                  CPM: ${data.summary.cpm?.toFixed(2) || '0.00'}
                  {data.summary.cpmr > 0 && (
                    <> · CPMr: ${data.summary.cpmr?.toFixed(2)} ({data.summary.frequency?.toFixed(1)}x)</>
                  )}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Spend by Channel Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Spend Distribution</CardTitle>
                <CardDescription>
                  Ad spend breakdown by marketing channel
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pieChartData.length > 0 ? (
                  <div className="h-80 relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={renderCustomLabel}
                          innerRadius={60}
                          outerRadius={110}
                          paddingAngle={2}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.color}
                              stroke="hsl(var(--background))"
                              strokeWidth={2}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} wrapperStyle={{ zIndex: 100 }} />
                        <Legend 
                          verticalAlign="bottom"
                          height={36}
                          formatter={(value) => <span className="text-foreground text-sm">{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Center label showing total - z-0 so tooltips appear above */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0" style={{ marginBottom: '36px' }}>
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground uppercase tracking-wider">Total</div>
                        <div className="text-xl font-bold text-foreground">
                          {formatCurrency(pieChartData.reduce((sum, d) => sum + d.value, 0))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-80 flex items-center justify-center text-muted-foreground">
                    No channel data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Channel Breakdown Table */}
            <Card>
              <CardHeader>
                <CardTitle>Channel Performance</CardTitle>
                <CardDescription>
                  Detailed metrics by channel
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Phones: a card per channel; eight columns do not fit a phone. */}
                <div className="space-y-2 md:hidden">
                  {data.by_channel?.map((ch, index) => {
                    const series = quality?.channels?.[trafficSourceForChannel(ch.channel)]
                    const scored = (series || []).filter(v => v !== null && v !== undefined)
                    const metric = (label, value) => (
                      <div className="min-w-0">
                        <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
                        <dd className="font-mono text-sm tabular-nums">{value}</dd>
                      </div>
                    )
                    return (
                      <div key={ch.channel} className="rounded-lg border px-3 py-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-2">
                            <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: getChannelColor(ch.channel, index) }} />
                            <span className="truncate font-medium">{ch.name}</span>
                          </span>
                          <span className="flex shrink-0 items-center gap-2">
                            <span className="font-mono text-base font-semibold tabular-nums">{formatCurrency(ch.spend)}</span>
                            <Badge variant="secondary" className="text-xs">{ch.percent_of_total?.toFixed(1)}%</Badge>
                          </span>
                        </div>
                        <dl className="mt-2 grid grid-cols-3 gap-x-3 gap-y-2">
                          {metric('Clicks', formatNumber(ch.clicks))}
                          {metric('CPC', `$${ch.cpc?.toFixed(2) || '0.00'}`)}
                          {metric('CPM', `$${ch.cpm?.toFixed(2) || '0.00'}`)}
                          {metric('CPMr', ch.cpmr > 0 ? <span title={`Freq: ${ch.frequency?.toFixed(1)}x`}>${ch.cpmr?.toFixed(2)}</span> : '—')}
                          {metric('Quality', scored.length
                            ? <span className="inline-flex items-center gap-1.5">{scored[scored.length - 1].toFixed(2)}<QualitySparkline data={series} /></span>
                            : <span className="text-muted-foreground">—</span>)}
                        </dl>
                      </div>
                    )
                  })}
                </div>
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2 font-medium">Channel</th>
                        <th className="text-right py-2 px-2 font-medium">Spend</th>
                        <th className="text-right py-2 px-2 font-medium">Clicks</th>
                        <th className="text-right py-2 px-2 font-medium">CPC</th>
                        <th className="text-right py-2 px-2 font-medium">CPM</th>
                        <th className="text-right py-2 px-2 font-medium">CPMr</th>
                        <th
                          className="text-right py-2 px-2 font-medium"
                          title="Traffic quality score — expected conversion %, based on how deep this channel's traffic gets on the site"
                        >
                          Quality
                        </th>
                        <th className="text-right py-2 px-2 font-medium">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_channel?.map((ch, index) => (
                        <tr key={ch.channel} className="border-b hover:bg-muted/50">
                          <td className="py-2 px-2">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: getChannelColor(ch.channel, index) }}
                              />
                              <span className="font-medium">{ch.name}</span>
                            </div>
                          </td>
                          <td className="text-right py-2 px-2">{formatCurrency(ch.spend)}</td>
                          <td className="text-right py-2 px-2">{formatNumber(ch.clicks)}</td>
                          <td className="text-right py-2 px-2">${ch.cpc?.toFixed(2) || '0.00'}</td>
                          <td className="text-right py-2 px-2">${ch.cpm?.toFixed(2) || '0.00'}</td>
                          <td className="text-right py-2 px-2">
                            {ch.cpmr > 0 ? (
                              <span title={`Freq: ${ch.frequency?.toFixed(1)}x`}>${ch.cpmr?.toFixed(2)}</span>
                            ) : '—'}
                          </td>
                          <td className="text-right py-2 px-2">
                            {(() => {
                              const series = quality?.channels?.[trafficSourceForChannel(ch.channel)]
                              if (!series) return <span className="text-muted-foreground">—</span>
                              const scored = series.filter(v => v !== null && v !== undefined)
                              if (!scored.length) return <span className="text-muted-foreground">—</span>
                              const latest = scored[scored.length - 1]
                              return (
                                <div className="flex items-center justify-end gap-1.5">
                                  <span className="font-mono text-xs">{latest.toFixed(2)}</span>
                                  <QualitySparkline data={series} />
                                </div>
                              )
                            })()}
                          </td>
                          <td className="text-right py-2 px-2">
                            <Badge variant="secondary" className="text-xs">
                              {ch.percent_of_total?.toFixed(1)}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Daily Spend Chart */}
          {dailyChartData.length > 0 && data.by_channel?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Daily Spend by Channel</CardTitle>
                <CardDescription>
                  Stacked area chart showing daily spend distribution
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dailyChartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={(value) => `$${formatNumber(value)}`}
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      {data.by_channel?.map((ch, index) => (
                        <Area
                          key={ch.channel}
                          type="monotone"
                          dataKey={ch.name}
                          stackId="1"
                          stroke={getChannelColor(ch.channel, index)}
                          fill={getChannelColor(ch.channel, index)}
                          fillOpacity={0.6}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Campaign Breakdown */}
          {campaigns?.campaigns?.length > 0 && (
            <CampaignBreakdown tree={campaigns} adQuality={quality?.ads} dateRange={dateRange} clinicId={currentClinicId} />
          )}

          {/* Weekly Trends */}
          {trendsChartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Weekly Trends</CardTitle>
                <CardDescription>
                  Spend and click trends over the past 8 weeks
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trendsChartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="week"
                        tick={{ fontSize: 11 }}
                        tickLine={false}
                      />
                      <YAxis
                        yAxisId="left"
                        tickFormatter={(value) => `$${formatNumber(value)}`}
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tickFormatter={(value) => formatNumber(value)}
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          name === 'Spend' ? formatCurrency(value) : formatNumber(value),
                          name
                        ]}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--popover))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="spend" fill="#6366F1" name="Spend" />
                      <Bar yAxisId="right" dataKey="clicks" fill="#10B981" name="Clicks" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
